/**
 * TagsTab — the Tag Rules screen, scoped to THIS campaign.
 *
 * Deliberately not a second, smaller rules UI. It renders the very same
 * components the admin Tag Rules page does — both families (Performance tags
 * and Flow optimisation), the same rows, the same editor — with the scope
 * PINNED to the campaign the drawer is open on. So everything you can read or
 * change here you already know how to, and there is one implementation to keep
 * correct instead of two that drift.
 *
 * Pinning also removes the country → campaign picker: you did not navigate here
 * to choose a campaign, you opened one. See features/tag-rule-scope/scope-pin.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';
import type { FlowLayer } from '@trafficpulse/shared';

import { SegmentedPills } from '@/components/ui';
import { TagRuleScopePin } from '@/features/tag-rule-scope';
import { FlowTagRuleTabContent } from '@/pages/admin/FlowTagRulesPage';
import { TagRuleTabContent } from '@/pages/admin/TagRulesPage/components/TagRuleTabContent';
import type { TagRuleTabVariant } from '@/hooks';
import { cn } from '@/lib/utils';

import { useFlowOptimizerLivePreview } from '../../context/FlowOptimizerLivePreviewContext';
import { type FlowOptimizerProvider } from '../../services/flow-optimizer-api';
import { tagsScopeFrom } from './model';

/** The layer pills, and the performance-tag variant each one maps onto. */
const LAYERS: ReadonlyArray<{
  value: FlowLayer;
  label: string;
  variant: TagRuleTabVariant;
}> = [
  { value: 'paths', label: 'Paths', variant: 'path' },
  { value: 'landers', label: 'Landers', variant: 'lander' },
  { value: 'offers', label: 'Offers', variant: 'offer' },
];

const FAMILIES = [
  { key: 'performance' as const, label: 'Performance tags' },
  { key: 'flow' as const, label: 'Flow optimisation' },
];

export interface TagsTabProps {
  campaignId: string;
  provider: FlowOptimizerProvider;
}

const TagsTab: FC<TagsTabProps> = ({ campaignId, provider }) => {
  const [layer, setLayer] = useState<FlowLayer>('paths');
  const [family, setFamily] = useState<'performance' | 'flow'>('flow');
  const { result } = useFlowOptimizerLivePreview();

  const { scope } = useMemo(
    () => tagsScopeFrom(result, campaignId, provider),
    [result, campaignId, provider],
  );
  const active = LAYERS.find((l) => l.value === layer) ?? LAYERS[0];

  return (
    <TagRuleScopePin scope={scope}>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <SegmentedPills<FlowLayer>
            label="Layer"
            options={LAYERS.map((l) => ({
              value: l.value,
              label: l.label,
              tone: 'primary',
            }))}
            value={layer}
            onChange={setLayer}
          />
          <div className="flex w-fit gap-1 rounded-lg border border-border bg-muted/30 p-1">
            {FAMILIES.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFamily(f.key)}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                  family === f.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-muted-foreground">
            Values shown are the ones this campaign is judged on.
          </span>
        </div>
        {family === 'flow' ? (
          <FlowTagRuleTabContent layer={active.value} />
        ) : (
          <TagRuleTabContent variant={active.variant} />
        )}
      </div>
    </TagRuleScopePin>
  );
};

export default TagsTab;
